<script setup lang="ts">
import { reactive, ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { quoteMinQuantity } from '~~/app/brands'
import {
  QUOTE_MESSAGE_MAX_LENGTH,
  todayIsoDate,
  validateQuoteRequest,
  type QuoteFieldKey,
} from '~~/shared/utils/quoteRequest'

/**
 * Formularz zapytania ofertowego — konwersja sklepu katalogowego.
 *
 * Walidacja korzysta z tego samego modułu co proxy Nitro
 * (shared/utils/quoteRequest), więc użytkownik widzi identyczny komunikat
 * niezależnie od tego, czy odrzuciła go przeglądarka, serwer, czy PrestaShop.
 *
 * Do backendu jedzie sam `id_product` — nazwę i SKU czyta on z bazy w chwili
 * zapytania. Pokazana tu nazwa produktu służy wyłącznie temu, żeby pytający
 * wiedział, czego dotyczy formularz, i nie musiał jej przepisywać.
 */

const { context, isOpen, close } = useQuoteRequest()
const analytics = useAnalytics()

/**
 * Pozycje Listy Zapytania, gdy modal otwarto ze strony listy.
 *
 * null oznacza zwykłe zapytanie o jeden produkt — wtedy formularz zachowuje się
 * dokładnie jak dotąd (pole „Nakład" i `id_product`).
 */
const quoteItems = computed(() => {
  const items = context.value?.items

  return items && items.length > 0 ? items : null
})

/**
 * Minimalny nakład marki (proles: 50, zgodnie z „od 50 — sztuk minimalnego
 * nakładu" na stronie „O nas"). Reguła formularza — proxy i backend przyjmują
 * każdy dodatni nakład.
 */
const minQuantity = quoteMinQuantity(useBrand())

/** Nakład startowy pola: minimum marki, a bez minimum — puste pole jak dotąd. */
function initialQuantity(): string {
  return minQuantity > 1 ? String(minQuantity) : ''
}

/**
 * Cloudflare Turnstile — renderowany tylko wtedy, gdy skonfigurowano klucz
 * publiczny. Bez klucza widget nie powstaje, a backend (z pustym sekretem)
 * captchy nie wymaga, więc dev i świeże QA działają bez kluczy Cloudflare.
 *
 * Token jest JEDNORAZOWY: po każdej odrzuconej próbie trzeba wziąć nowy,
 * inaczej Cloudflare odpowie „timeout-or-duplicate" na poprawnie wypełniony
 * formularz.
 */
interface TurnstileApi {
  render: (el: HTMLElement, options: Record<string, unknown>) => string
  reset: (widgetId?: string) => void
  remove: (widgetId?: string) => void
}

const turnstileSiteKey = useRuntimeConfig().public.turnstileSiteKey as string
const turnstileEnabled = Boolean(turnstileSiteKey)
const turnstileToken = ref('')
const turnstileEl = ref<HTMLElement | null>(null)
let turnstileWidgetId: string | null = null

if (turnstileEnabled) {
  useHead({
    script: [{
      src: 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit',
      async: true,
      defer: true,
    }],
  })
}

function turnstileApi(): TurnstileApi | null {
  if (!import.meta.client) return null

  return (window as unknown as { turnstile?: TurnstileApi }).turnstile ?? null
}

async function mountTurnstile() {
  if (!turnstileEnabled || !import.meta.client) return

  // Skrypt ładuje się asynchronicznie — poczekaj na niego, ale nie w
  // nieskończoność: brak widgetu nie może zablokować formularza, bo o
  // przepuszczeniu i tak decyduje backend.
  for (let attempt = 0; attempt < 40 && !turnstileApi(); attempt++) {
    await new Promise(resolve => setTimeout(resolve, 100))
  }

  const api = turnstileApi()
  if (!api || !turnstileEl.value) return

  turnstileWidgetId = api.render(turnstileEl.value, {
    sitekey: turnstileSiteKey,
    callback: (token: string) => {
      turnstileToken.value = token
    },
    'expired-callback': () => {
      turnstileToken.value = ''
    },
    'error-callback': () => {
      turnstileToken.value = ''
    },
  })
}

function resetTurnstile() {
  turnstileToken.value = ''
  const api = turnstileApi()
  if (api && turnstileWidgetId !== null) {
    api.reset(turnstileWidgetId)
  }
}

const form = reactive({
  name: '',
  company: '',
  nip: '',
  email: '',
  phone: '',
  quantity: initialQuantity(),
  message: '',
  consent: false,
  // Honeypot — prawdziwy użytkownik nigdy tego nie wypełni.
  website: '',
})

const errors = reactive<Partial<Record<QuoteFieldKey | 'form' | 'captcha', string>>>({})
const pending = ref(false)
const sent = ref(false)
const firstFieldRef = ref<HTMLInputElement | null>(null)
const dialogRef = ref<HTMLElement | null>(null)

// Podpowiedź o minimum czyta czytnik ekranu razem z polem, a błąd — gdy jest.
const quantityDescribedBy = computed(() => {
  const ids = [errors.quantity ? 'qr-err-quantity' : '', minQuantity > 1 ? 'qr-hint-quantity' : ''].filter(Boolean)

  return ids.length > 0 ? ids.join(' ') : undefined
})

function clearError(field: QuoteFieldKey) {
  errors[field] = undefined
}

const formErrorRef = ref<HTMLElement | null>(null)

/**
 * Klucze błędów, które formularz pokazuje PRZY POLU. Nakład znika przy Liście
 * Zapytania, captcha — bez klucza Turnstile.
 */
function fieldErrorKeys(): Set<string> {
  const keys = new Set(['name', 'email', 'phone', 'company', 'nip', 'message', 'consent'])
  if (!quoteItems.value) {
    keys.add('quantity')
  }
  if (turnstileEnabled) {
    keys.add('captcha')
  }

  return keys
}

/**
 * Rozkłada błędy na pola i komunikat ogólny.
 *
 * Klucz bez pola w formularzu (id_product_attribute — „wariant nie jest już
 * dostępny", id_product, items, attachment, każdy nieznany) wpisany wprost do
 * `errors` nie renderował się nigdzie: brak komunikatu, fokus bez celu,
 * wysyłka stała po cichu. Takie komunikaty idą do `form`, sklejone bez
 * powtórzeń.
 */
function applyErrors(found: Record<string, string | undefined>) {
  const fieldKeys = fieldErrorKeys()
  const general: string[] = []

  for (const [key, message] of Object.entries(found)) {
    if (!message) continue
    if (fieldKeys.has(key)) {
      errors[key as QuoteFieldKey | 'captcha'] = message
    }
    else if (!general.includes(message)) {
      general.push(message)
    }
  }

  if (general.length > 0) {
    errors.form = general.join(' ')
  }
}

/**
 * Fokus na pierwszym błędnym polu, a gdy błąd jest tylko ogólny — na jego
 * komunikacie. Bez tego użytkownik czytnika ekranu nie dowie się, że formularz
 * w ogóle został odrzucony.
 */
async function focusFirstError() {
  await nextTick()
  const firstInvalid = dialogRef.value?.querySelector<HTMLElement>('[aria-invalid="true"]')
  ;(firstInvalid ?? formErrorRef.value)?.focus()
}

function resetForm() {
  Object.assign(form, {
    name: '',
    company: '',
    nip: '',
    email: '',
    phone: '',
    quantity: initialQuantity(),
    message: '',
    consent: false,
    website: '',
  })
  for (const key of Object.keys(errors) as (QuoteFieldKey | 'form' | 'captcha')[]) {
    errors[key] = undefined
  }
  sent.value = false
}

async function submit() {
  if (pending.value) return

  const { errors: found } = validateQuoteRequest(
    {
      ...form,
      id_product: context.value?.idProduct ?? null,
      id_product_attribute: context.value?.idProductAttribute ?? null,
      items: quoteItems.value,
      // Przy Liście Zapytania nakład jest przy pozycjach, a pola w formularzu
      // nie ma — pusty ciąg wywołałby błąd „podaj nakład".
      quantity: quoteItems.value ? undefined : form.quantity,
    },
    todayIsoDate(),
    { minQuantity },
  )

  for (const key of Object.keys(errors) as (QuoteFieldKey | 'form' | 'captcha')[]) {
    errors[key] = undefined
  }

  if (Object.keys(found).length > 0) {
    applyErrors(found)
    await focusFirstError()
    return
  }

  pending.value = true
  try {
    await $fetch('/api/store/quote-request', {
      method: 'POST',
      body: {
        ...form,
        id_product: context.value?.idProduct ?? undefined,
        // Sam identyfikator wariantu — opis i SKU backend czyta z bazy.
        id_product_attribute: context.value?.idProductAttribute || undefined,
        items: quoteItems.value ?? undefined,
        quantity: quoteItems.value ? undefined : form.quantity,
        turnstile_token: turnstileToken.value || undefined,
      },
    })
    sent.value = true
    // Konwersja katalogu — dopiero po przyjęciu zapytania przez backend.
    analytics.trackGenerateLead({
      leadSource: quoteItems.value ? 'quote_list' : 'product',
      itemsCount: quoteItems.value?.length ?? 1,
    })
  }
  catch (err: unknown) {
    // Token jest jednorazowy — po każdej odmowie bierzemy nowy, inaczej kolejna
    // próba dostanie „timeout-or-duplicate" mimo poprawnie wypełnionego formularza.
    resetTurnstile()

    const data = (err as { data?: { data?: { errors?: Record<string, string> } } }).data?.data
    if (data?.errors && Object.keys(data.errors).length > 0) {
      applyErrors(data.errors)
    }
    else {
      const status = (err as { statusCode?: number }).statusCode
      errors.form = status === 429
        ? 'Wysłano już kilka zapytań z tego adresu. Spróbuj ponownie za kilkanaście minut.'
        : 'Nie udało się wysłać zapytania. Spróbuj ponownie albo napisz do nas przez zakładkę Kontakt.'
    }
  }
  finally {
    pending.value = false
  }

  if (!sent.value) {
    await focusFirstError()
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (isOpen.value && event.key === 'Escape') close()
}

watch(isOpen, async (open) => {
  if (!import.meta.client) return
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) {
    resetForm()
    await nextTick()
    firstFieldRef.value?.focus()
    void mountTurnstile()
  }
  else {
    turnstileWidgetId = null
    turnstileToken.value = ''
  }
})

onMounted(() => document.addEventListener('keydown', handleKeydown))
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  if (import.meta.client) document.body.style.overflow = ''
})
</script>

<template>
  <Teleport to="body">
    <Transition name="qr-fade">
      <div v-if="isOpen" class="qr-overlay" @click.self="close">
        <div
          ref="dialogRef"
          class="qr-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="qr-title"
        >
          <button type="button" class="qr-dialog__close" aria-label="Zamknij" @click="close">
            ×
          </button>

          <!-- Stan po wysłaniu: potwierdzenie z informacją o czasie odpowiedzi -->
          <div v-if="sent" class="qr-sent">
            <h2 id="qr-title" class="qr-dialog__title">
              Dziękujemy, zapytanie zostało wysłane
            </h2>
            <p>
              Odpowiadamy zwykle w ciągu jednego dnia roboczego. Potwierdzenie
              wysłaliśmy na podany adres e-mail.
            </p>
            <button type="button" class="qr-submit" @click="close">
              Zamknij
            </button>
          </div>

          <form v-else class="qr-form" novalidate @submit.prevent="submit">
            <h2 id="qr-title" class="qr-dialog__title">
              Zapytaj o wycenę
            </h2>

            <p v-if="context?.name" class="qr-product">
              Zapytanie dotyczy: <strong>{{ context.name }}</strong>
              <!-- Wariant widoczny wprost, nie tylko w SKU — inaczej klient nie
                   wie, jaki rozmiar idzie do wyceny. -->
              <span v-if="context?.attributes" class="qr-product__variant">— {{ context.attributes }}</span>
              <span v-if="context?.reference" class="qr-product__sku">(SKU: {{ context.reference }})</span>
            </p>

            <p
              v-if="errors.form"
              ref="formErrorRef"
              class="qr-error qr-error--form"
              role="alert"
              tabindex="-1"
            >
              {{ errors.form }}
            </p>

            <div class="qr-grid">
              <label class="qr-field">
                <span class="qr-field__label">Imię i nazwisko <abbr title="pole wymagane">*</abbr></span>
                <input
                  ref="firstFieldRef"
                  v-model="form.name"
                  type="text"
                  autocomplete="name"
                  required
                  :aria-invalid="Boolean(errors.name)"
                  :aria-describedby="errors.name ? 'qr-err-name' : undefined"
                  @input="clearError('name')"
                >
                <span v-if="errors.name" id="qr-err-name" class="qr-error">{{ errors.name }}</span>
              </label>

              <label class="qr-field">
                <span class="qr-field__label">E-mail <abbr title="pole wymagane">*</abbr></span>
                <input
                  v-model="form.email"
                  type="email"
                  autocomplete="email"
                  required
                  :aria-invalid="Boolean(errors.email)"
                  :aria-describedby="errors.email ? 'qr-err-email' : undefined"
                  @input="clearError('email')"
                >
                <span v-if="errors.email" id="qr-err-email" class="qr-error">{{ errors.email }}</span>
              </label>

              <!-- Przy Liście Zapytania nakład jest przy każdej pozycji, więc
                   jedno globalne pole tylko wprowadzałoby w błąd. -->
              <label
                v-if="!quoteItems"
                class="qr-field"
              >
                <span class="qr-field__label">Nakład (szt.) <abbr title="pole wymagane">*</abbr></span>
                <input
                  v-model="form.quantity"
                  type="text"
                  inputmode="numeric"
                  required
                  placeholder="np. 250"
                  :aria-invalid="Boolean(errors.quantity)"
                  :aria-describedby="quantityDescribedBy"
                  @input="clearError('quantity')"
                >
                <!-- Ta sama obietnica co na stronie „O nas": od 50 sztuk. -->
                <span v-if="minQuantity > 1" id="qr-hint-quantity" class="qr-hint">Minimalny nakład: {{ minQuantity }} szt.</span>
                <span v-if="errors.quantity" id="qr-err-quantity" class="qr-error">{{ errors.quantity }}</span>
              </label>

              <label class="qr-field">
                <span class="qr-field__label">Telefon</span>
                <input
                  v-model="form.phone"
                  type="tel"
                  autocomplete="tel"
                  :aria-invalid="Boolean(errors.phone)"
                  :aria-describedby="errors.phone ? 'qr-err-phone' : undefined"
                  @input="clearError('phone')"
                >
                <span v-if="errors.phone" id="qr-err-phone" class="qr-error">{{ errors.phone }}</span>
              </label>

              <!-- Firma obowiązkowa — sklep obsługuje wyłącznie klientów firmowych
                   (uwaga klientki 20.10.2026). NIP zostaje opcjonalny. -->
              <label class="qr-field">
                <span class="qr-field__label">Firma <abbr title="pole wymagane">*</abbr></span>
                <input
                  v-model="form.company"
                  type="text"
                  autocomplete="organization"
                  required
                  :aria-invalid="Boolean(errors.company)"
                  :aria-describedby="errors.company ? 'qr-err-company' : undefined"
                  @input="clearError('company')"
                >
                <span v-if="errors.company" id="qr-err-company" class="qr-error">{{ errors.company }}</span>
              </label>

              <label class="qr-field">
                <span class="qr-field__label">NIP</span>
                <input
                  v-model="form.nip"
                  type="text"
                  inputmode="numeric"
                  placeholder="np. 525-224-84-81"
                  :aria-invalid="Boolean(errors.nip)"
                  :aria-describedby="errors.nip ? 'qr-err-nip' : undefined"
                  @input="clearError('nip')"
                >
                <span v-if="errors.nip" id="qr-err-nip" class="qr-error">{{ errors.nip }}</span>
              </label>

              <!-- Bez pól „Technika znakowania" i „Termin realizacji" (uwaga
                   klientki 20.10.2026): technikę dobiera handlowiec przy wycenie,
                   a klient może ją opisać w treści zapytania. -->
            </div>

            <label class="qr-field qr-field--wide">
              <span class="qr-field__label">Treść zapytania <abbr title="pole wymagane">*</abbr></span>
              <textarea
                v-model="form.message"
                rows="4"
                required
                :maxlength="QUOTE_MESSAGE_MAX_LENGTH"
                placeholder="Napisz, czego dotyczy zapytanie — kolor, nadruk lub technika znakowania, termin, dodatkowe wymagania."
                :aria-invalid="Boolean(errors.message)"
                :aria-describedby="errors.message ? 'qr-err-message' : undefined"
                @input="clearError('message')"
              />
              <span v-if="errors.message" id="qr-err-message" class="qr-error">{{ errors.message }}</span>
            </label>

            <label class="qr-consent">
              <input
                v-model="form.consent"
                type="checkbox"
                :aria-invalid="Boolean(errors.consent)"
                :aria-describedby="errors.consent ? 'qr-err-consent' : undefined"
                @change="clearError('consent')"
              >
              <span>
                Wyrażam zgodę na przetwarzanie moich danych w celu przygotowania i przesłania oferty.
                <NuxtLink to="/polityka-prywatnosci" target="_blank">Polityka prywatności</NuxtLink>
              </span>
            </label>
            <span v-if="errors.consent" id="qr-err-consent" class="qr-error">{{ errors.consent }}</span>

            <!-- Honeypot: ukryty przed człowiekiem, niewidoczny dla czytników ekranu -->
            <div class="qr-honeypot" aria-hidden="true">
              <label>
                Strona WWW
                <input v-model="form.website" type="text" tabindex="-1" autocomplete="off">
              </label>
            </div>

            <!-- Turnstile: kontener istnieje tylko przy skonfigurowanym kluczu.
                 Błąd captchy pokazujemy przy widgecie, nie w ogólnym komunikacie. -->
            <div v-if="turnstileEnabled" class="qr-turnstile">
              <div ref="turnstileEl" />
              <span v-if="errors.captcha" class="qr-error" role="alert">{{ errors.captcha }}</span>
            </div>

            <div class="qr-actions">
              <button type="submit" class="qr-submit" :disabled="pending">
                {{ pending ? 'Wysyłanie…' : 'Wyślij zapytanie' }}
              </button>
              <!-- Ścieżka zapasowa, gdy modal zawiedzie albo ktoś woli formularz na stronie -->
              <NuxtLink to="/contact" class="qr-fallback" @click="close">
                Wolisz napisać przez zakładkę Kontakt?
              </NuxtLink>
            </div>
          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.qr-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgb(0 0 0 / 55%);
  overflow-y: auto;
}

.qr-dialog {
  position: relative;
  width: min(680px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.75rem;
  border-radius: 12px;
  background: #fff;
}

.qr-dialog__close {
  position: absolute;
  top: 0.5rem;
  right: 0.75rem;
  border: 0;
  background: none;
  font-size: 1.75rem;
  line-height: 1;
  cursor: pointer;
}

.qr-dialog__title {
  margin: 0 0 0.75rem;
  font-size: 1.35rem;
}

.qr-product {
  margin: 0 0 1rem;
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  background: #f5f5f5;
  font-size: 0.95rem;
}

.qr-product__sku {
  margin-left: 0.35rem;
  color: #666;
}

.qr-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

@media (max-width: 640px) {
  .qr-grid {
    grid-template-columns: 1fr;
  }
}

.qr-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.9rem;
}

.qr-field--wide {
  margin-top: 0.85rem;
}

.qr-field__label {
  font-weight: 600;
}

.qr-field input,
.qr-field select,
.qr-field textarea {
  padding: 0.55rem 0.7rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: #fff;
  font: inherit;
}

.qr-field input[aria-invalid='true'],
.qr-field select[aria-invalid='true'],
.qr-field textarea[aria-invalid='true'] {
  border-color: #c0392b;
}

.qr-hint {
  color: #666;
  font-size: 0.8rem;
}

.qr-product__variant {
  margin-left: 0.35rem;
}

.qr-error {
  color: #c0392b;
  font-size: 0.85rem;
}

.qr-error--form {
  display: block;
  margin-bottom: 0.75rem;
}

.qr-consent {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  margin-top: 1rem;
  font-size: 0.85rem;
}

.qr-honeypot {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.qr-turnstile {
  margin-top: 1rem;
}

.qr-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  margin-top: 1.25rem;
}

/* „Wyślij zapytanie" w barwach przycisku głównego marki (proles: turkus
   z napisem w ciemnym tealu, 03.11.2026). Fallback = dotychczasowa czerń. */
.qr-submit {
  padding: 0.7rem 1.4rem;
  border: 0;
  border-radius: 6px;
  background: var(--btn-primary-bg, #111);
  color: var(--btn-primary-text, #fff);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.qr-submit:disabled {
  opacity: 0.6;
  cursor: default;
}

.qr-fallback {
  font-size: 0.85rem;
  text-decoration: underline;
}

.qr-sent p {
  margin: 0 0 1.25rem;
}

.qr-fade-enter-active,
.qr-fade-leave-active {
  transition: opacity 0.18s ease;
}

.qr-fade-enter-from,
.qr-fade-leave-to {
  opacity: 0;
}
</style>
